import type { Locale } from '@messenger/types'
import { LOCALE, LOCALE_VALUES } from '@messenger/consts'
import { frenchDateTranslations } from 'mocks/translations/frenchDateTranslations'
import { spanishDateTranslations } from 'mocks/translations/spanishDateTranslations'
import { frenchFormatMappings } from 'mocks/translations/frenchDateFormatMapping'
import { spanishFormatMappings } from 'mocks/translations/spanishDateFormatMapping'
import regEx from 'utils/global/regEx'
import * as dateFnsLocales from 'date-fns/locale'
import type { Locale as DateFnsLocale } from 'date-fns'

export type DateTranslations = {
  months: {
    full: { [key: string]: string }
    short: { [key: string]: string }
  }
  days: {
    full: { [key: string]: string }
    short: { [key: string]: string }
  }
}

// prisma locale mapped values mapped to date-fns locale values
type DateFnsLocaleMapping = {
  [K in Locale]: keyof typeof dateFnsLocales
}

const dateFnsLocaleMapping: DateFnsLocaleMapping = {
  EN: 'enUS',
  FR: 'fr',
  ES: 'es',
}

const translationsMap: Record<Locale, DateTranslations | undefined> = {
  // replace undefined with translations when it comes up
  EN: undefined,
  ES: spanishDateTranslations,
  FR: frenchDateTranslations,
}

export const getDateFnsLocale = (locale: Locale): DateFnsLocale | undefined => {
  if (locale in dateFnsLocaleMapping) {
    const localeKey = dateFnsLocaleMapping[locale]
    return dateFnsLocales[localeKey]
  }
  return undefined
}

export const applyDateTranslations = (
  formattedDate: string,
  translations: DateTranslations,
  locale: Locale,
): string => {
  let translatedDate = formattedDate

  const applyReplacements = (dictionary: Record<string, string>) => {
    Object.entries(dictionary).forEach(([en, translated]) => {
      translatedDate = translatedDate.replace(
        regEx.createWordBoundaryRegex(en),
        translated,
      )
    })
  }

  // apply literal translation replacements for day and month
  applyReplacements(translations.months.full)
  applyReplacements(translations.months.short)
  applyReplacements(translations.days.full)
  applyReplacements(translations.days.short)

  // format the new translated date
  if (locale === LOCALE.FR) {
    translatedDate = translatedDate
      .replace(regEx.handleDayOfWeekDateFrench, '$1 $3 $2 $4')
      .replace(regEx.handleMonthDayYearFrenchAndSpanish, '$2 $1 $3')
      .replace(regEx.removeCommas, '')
  } else if (locale === LOCALE.ES) {
    translatedDate = translatedDate
      .replace(regEx.handleDayOfWeekDateSpanish, '$1 $3 de $2 de $4')
      .replace(regEx.handleMonthDayYearFrenchAndSpanish, '$2 de $1 de $3')
      .replace(regEx.removeCommas, '')
  }

  return translatedDate
}

export const languageNeedsSpecialFormatting = (locale: Locale): boolean => {
  const nonSupportedLocale = !LOCALE_VALUES.includes(locale)
  if (nonSupportedLocale) {
    return false
  }
  const needsSpecialFormatting = locale === LOCALE.FR || locale === LOCALE.ES
  return needsSpecialFormatting
}

export const applyLanguageSpecificFormatting = (
  dateString: string,
  locale: Locale,
): string => {
  let result = dateString

  const nonSupportedLocale = !LOCALE_VALUES.includes(locale)
  const needsSpecialFormatting = languageNeedsSpecialFormatting(locale)
  const noSpecialFormattingNeeded =
    nonSupportedLocale || !needsSpecialFormatting
  if (noSpecialFormattingNeeded) {
    return result
  }

  const needsCommaRemoval = locale === LOCALE.FR || locale === LOCALE.ES
  if (needsCommaRemoval) {
    result = result.replace(regEx.removeCommas, '')
  }

  return result
}

export const getTranslationsForLanguage = (
  locale: Locale,
): DateTranslations | undefined => {
  const nonSupportedLocale = !LOCALE_VALUES.includes(locale)
  if (nonSupportedLocale) {
    return undefined
  }
  const code = locale
  return translationsMap[code]
}

export const getLocaleDateFormat = (
  locale: Locale,
  dateFormat: string,
): string => {
  let convertedFormat = dateFormat
  const isFrench = locale === LOCALE.FR
  const isSpanish = locale === LOCALE.ES
  if (isFrench) {
    if (dateFormat in frenchFormatMappings) {
      convertedFormat = frenchFormatMappings[dateFormat]
    }
  }
  if (isSpanish) {
    if (dateFormat in spanishFormatMappings) {
      convertedFormat = spanishFormatMappings[dateFormat]
    }
  }
  return convertedFormat
}
